// components.jsx — shared UI for the Nurse CNE prototype

const { useState, useEffect, useRef, useMemo, useCallback, Fragment } = React;

// ─────────────────────────────────────────────────────────────────────────────
// Icons (stroke-based, matched to body font weight)
// ─────────────────────────────────────────────────────────────────────────────
const Icon = ({ name, size = 20, stroke = 1.6, color = 'currentColor', fill = 'none' }) => {
  const s = { width: size, height: size, fill, stroke: color, strokeWidth: stroke, strokeLinecap: 'round', strokeLinejoin: 'round' };
  switch (name) {
    case 'home':     return <svg viewBox="0 0 24 24" {...s}><path d="M3 11l9-7 9 7v9a2 2 0 01-2 2h-4v-7h-6v7H5a2 2 0 01-2-2v-9z"/></svg>;
    case 'compass':  return <svg viewBox="0 0 24 24" {...s}><circle cx="12" cy="12" r="9"/><path d="M15.5 8.5l-2 5-5 2 2-5z"/></svg>;
    case 'plus':     return <svg viewBox="0 0 24 24" {...s}><path d="M12 5v14M5 12h14"/></svg>;
    case 'book':     return <svg viewBox="0 0 24 24" {...s}><path d="M4 4h12a3 3 0 013 3v13H7a3 3 0 01-3-3V4z"/><path d="M4 17a3 3 0 013-3h12"/></svg>;
    case 'user':     return <svg viewBox="0 0 24 24" {...s}><circle cx="12" cy="8" r="4"/><path d="M4 21c1.5-4 5-6 8-6s6.5 2 8 6"/></svg>;
    case 'users':    return <svg viewBox="0 0 24 24" {...s}><circle cx="9" cy="8" r="3.4"/><path d="M3 20c1.2-3.3 3.8-5 6-5s4.8 1.7 6 5"/><path d="M16 5.2a3.2 3.2 0 010 6M18.5 20c-.5-2-1.6-3.6-3-4.6"/></svg>;
    case 'bookmark': return <svg viewBox="0 0 24 24" {...s}><path d="M6 3h12v18l-6-4-6 4V3z"/></svg>;
    case 'bell':     return <svg viewBox="0 0 24 24" {...s}><path d="M6 9a6 6 0 1112 0c0 5 2 6 2 6H4s2-1 2-6z"/><path d="M10 19a2 2 0 004 0"/></svg>;
    case 'search':   return <svg viewBox="0 0 24 24" {...s}><circle cx="11" cy="11" r="7"/><path d="M20 20l-3.5-3.5"/></svg>;
    case 'filter':   return <svg viewBox="0 0 24 24" {...s}><path d="M3 5h18M6 12h12M10 19h4"/></svg>;
    case 'chev-r':   return <svg viewBox="0 0 24 24" {...s}><path d="M9 6l6 6-6 6"/></svg>;
    case 'chev-l':   return <svg viewBox="0 0 24 24" {...s}><path d="M15 6l-6 6 6 6"/></svg>;
    case 'chev-d':   return <svg viewBox="0 0 24 24" {...s}><path d="M6 9l6 6 6-6"/></svg>;
    case 'close':    return <svg viewBox="0 0 24 24" {...s}><path d="M6 6l12 12M6 18L18 6"/></svg>;
    case 'pin':      return <svg viewBox="0 0 24 24" {...s}><path d="M12 22s7-7.5 7-13a7 7 0 10-14 0c0 5.5 7 13 7 13z"/><circle cx="12" cy="9" r="2.5"/></svg>;
    case 'globe':    return <svg viewBox="0 0 24 24" {...s}><circle cx="12" cy="12" r="9"/><path d="M3 12h18M12 3a14 14 0 010 18M12 3a14 14 0 000 18"/></svg>;
    case 'clock':    return <svg viewBox="0 0 24 24" {...s}><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/></svg>;
    case 'calendar': return <svg viewBox="0 0 24 24" {...s}><rect x="3" y="5" width="18" height="16" rx="2"/><path d="M3 10h18M8 3v4M16 3v4"/></svg>;
    case 'check':    return <svg viewBox="0 0 24 24" {...s}><path d="M5 12.5l4.5 4.5L20 6.5"/></svg>;
    case 'check-circ': return <svg viewBox="0 0 24 24" {...s}><circle cx="12" cy="12" r="9"/><path d="M8 12.5l3 3 5-6"/></svg>;
    case 'cert':     return <svg viewBox="0 0 24 24" {...s}><circle cx="12" cy="9" r="5"/><path d="M9 13l-2 8 5-3 5 3-2-8"/></svg>;
    case 'download': return <svg viewBox="0 0 24 24" {...s}><path d="M12 4v12M7 11l5 5 5-5M5 21h14"/></svg>;
    case 'upload':   return <svg viewBox="0 0 24 24" {...s}><path d="M12 21V8M7 13l5-5 5 5M5 4h14"/></svg>;
    case 'sparkle':  return <svg viewBox="0 0 24 24" {...s}><path d="M12 3l1.7 5.3L19 10l-5.3 1.7L12 17l-1.7-5.3L5 10l5.3-1.7L12 3z"/></svg>;
    case 'list':     return <svg viewBox="0 0 24 24" {...s}><path d="M4 6h16M4 12h16M4 18h16"/></svg>;
    case 'grid':     return <svg viewBox="0 0 24 24" {...s}><rect x="4" y="4" width="7" height="7" rx="1"/><rect x="13" y="4" width="7" height="7" rx="1"/><rect x="4" y="13" width="7" height="7" rx="1"/><rect x="13" y="13" width="7" height="7" rx="1"/></svg>;
    case 'arrow-r':  return <svg viewBox="0 0 24 24" {...s}><path d="M5 12h14M13 6l6 6-6 6"/></svg>;
    case 'dot-vert': return <svg viewBox="0 0 24 24" {...s}><circle cx="12" cy="5" r="1.4" fill={color} stroke="none"/><circle cx="12" cy="12" r="1.4" fill={color} stroke="none"/><circle cx="12" cy="19" r="1.4" fill={color} stroke="none"/></svg>;
    case 'spark':    return <svg viewBox="0 0 24 24" {...s}><path d="M5 12l3 3 4-7 3 5 4-3"/></svg>;
    case 'pdf':      return <svg viewBox="0 0 24 24" {...s}><path d="M7 3h7l5 5v13H7z"/><path d="M14 3v5h5"/><path d="M9 14h1.5a1.5 1.5 0 010 3H9zM13.5 14H15v3h-1.5z" strokeWidth="1.2"/></svg>;
    case 'leaf':     return <svg viewBox="0 0 24 24" {...s}><path d="M5 19c0-7 5-12 14-13-1 9-6 14-13 14-1 0-1-1-1-1z"/><path d="M5 19l8-8"/></svg>;
    case 'shield':   return <svg viewBox="0 0 24 24" {...s}><path d="M12 3l8 3v6c0 4.5-3.5 8-8 9-4.5-1-8-4.5-8-9V6l8-3z"/></svg>;
    case 'moon':     return <svg viewBox="0 0 24 24" {...s}><path d="M21 12.8A9 9 0 1111.2 3a7 7 0 009.8 9.8z"/></svg>;
    case 'edit':     return <svg viewBox="0 0 24 24" {...s}><path d="M14 4l6 6-10 10H4v-6L14 4z"/><path d="M13 5l6 6"/></svg>;
    case 'mail':     return <svg viewBox="0 0 24 24" {...s}><rect x="3" y="5" width="18" height="14" rx="2"/><path d="M3 7l9 7 9-7"/></svg>;
    case 'trash':    return <svg viewBox="0 0 24 24" {...s}><path d="M4 7h16M10 11v6M14 11v6M6 7l1 13a2 2 0 002 2h6a2 2 0 002-2l1-13M9 7V4a1 1 0 011-1h4a1 1 0 011 1v3"/></svg>;
    case 'tag':      return <svg viewBox="0 0 24 24" {...s}><path d="M4 12V5a1 1 0 011-1h7l8 8-8 8-8-8z"/><circle cx="8.5" cy="8.5" r="1.2" fill={color} stroke="none"/></svg>;
    case 'heart':    return <svg viewBox="0 0 24 24" {...s}><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>;
    case 'thumb-up': return <svg viewBox="0 0 24 24" {...s}><path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3zM7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3"/></svg>;
    case 'star':     return <svg viewBox="0 0 24 24" {...s}><path d="M12 3l2.7 5.6 6.1.9-4.4 4.3 1 6.1L12 17l-5.4 2.9 1-6.1L3.2 9.5l6.1-.9L12 3z"/></svg>;
    case 'refresh':  return <svg viewBox="0 0 24 24" {...s}><path d="M20 12a8 8 0 1 1-2.34-5.66L20 8.5"/><path d="M20 3.5v5h-5"/></svg>;
    // Warning / critical notices. The only glyph in this set that reads as
    // "stop and read this" rather than "here is some information".
    case 'alert':    return <svg viewBox="0 0 24 24" {...s}><path d="M12 3.6L21.4 20H2.6L12 3.6z"/><path d="M12 10v4.2"/><path d="M12 17.3h.01"/></svg>;
    default:         return <svg viewBox="0 0 24 24" {...s}><circle cx="12" cy="12" r="3"/></svg>;
  }
};

// ─────────────────────────────────────────────────────────────────────────────
// Topic pill (color-coded)
// ─────────────────────────────────────────────────────────────────────────────
function TopicPill({ topic, label, size = 'sm' }) {
  const tint = TOPIC_TINT[topic] || TOPIC_TINT.ethics;
  return (
    <span className="topic-pill" style={{
      background: tint.bg,
      color: tint.fg,
      fontSize: size === 'lg' ? 12 : 11,
      padding: size === 'lg' ? '5px 11px' : undefined,
    }}>
      {label}
    </span>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// CategoryDot — core/elective/specialty marker
// ─────────────────────────────────────────────────────────────────────────────
const CAT_COLORS = {
  core:      'oklch(0.42 0.06 175)',  // jade
  elective:  'oklch(0.68 0.10 50)',   // clay
  specialty: 'oklch(0.45 0.09 270)',  // indigo
};
function CatDot({ cat, size = 7 }) {
  return <span className="dot-status" style={{ width: size, height: size, background: CAT_COLORS[cat] }} />;
}

// Accessible shared switch.  Buttons give us dependable keyboard behaviour;
// role/aria-checked let VoiceOver announce it as a switch rather than a vague
// pressed control. The 44px hit area is deliberate even where the visual pill
// is smaller.
function Toggle({ on, onChange, disabled = false, busy = false, ariaLabel, className = '' }) {
  return (
    <button type="button" className={'vital-toggle ' + className}
      role="switch" aria-checked={!!on} aria-busy={busy || undefined}
      aria-label={ariaLabel} disabled={disabled || busy}
      onClick={onChange}>
      <span className="vital-toggle-track" aria-hidden="true"><i /></span>
    </button>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// SourceBadge — small visual cue for where a course's data came from.
//
// Design intent: silence-is-default. The vast majority of catalog rows are
// `source='official' + verification_status='verified'` (the original 118
// NCHK-scraped courses), so this badge renders nothing for them — that
// keeps the existing card layout uncluttered. The badge only appears when
// the row is NOT the default, which is exactly when the user should pay
// attention to the provenance.
//
// Renders inline (no positioning) so callers can drop it into a row of
// chips next to TopicPill etc.
// ─────────────────────────────────────────────────────────────────────────────
function SourceBadge({ source, verification, lang, size = 'sm' }) {
  // Default state: render nothing.
  if ((source === 'official' || !source) && (verification === 'verified' || !verification)) {
    return null;
  }

  // Compute (label, tone) for the actually-occurring combinations.
  // `flagged` and `community_supported` are Phase 3 — included here so
  // the visual is ready once those flows ship, not because they appear today.
  let label_en = '';
  let label_zh = '';
  let tone = 'muted';

  if (verification === 'flagged') {
    label_en = 'Disputed'; label_zh = '資料有爭議'; tone = 'danger';
  } else if (verification === 'pending') {
    label_en = 'Pending review'; label_zh = '待審核'; tone = 'muted';
  } else if (verification === 'community_supported') {
    label_en = 'Community-supported'; label_zh = '社群多人支持'; tone = 'accent';
  } else if (source === 'community') {
    label_en = 'Community'; label_zh = '社群提交'; tone = 'primary';
  } else if (source === 'ai_extracted') {
    label_en = 'AI-extracted'; label_zh = 'AI 抽取'; tone = 'muted';
  } else {
    return null;
  }

  const fontSize = size === 'sm' ? 10.5 : 12;
  return (
    <span className={'cap-pill tone-' + tone}
          style={{ fontSize, display: 'inline-flex', alignItems: 'center', gap: 4 }}
          title={lang === 'zh' ? label_zh : label_en}>
      <Icon name="shield" size={9} stroke={1.8} />
      <span>{lang === 'zh' ? label_zh : label_en}</span>
    </span>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// hashHue — deterministic hue [0,359] from any string. Shared by ProviderAvatar
// (provider code) and UserAvatar (contributor) so the two swatches never drift.
// Same algorithm the server uses as its hue fallback in
// supabase-migration-community-social.sql (get_course_contributors); exact
// parity isn't required because the server always returns the resolved hue.
// ─────────────────────────────────────────────────────────────────────────────
function hashHue(str) {
  const s = String(str || 'XX');
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) % 360;
  return h;
}

// Curated avatar emoji — MUST stay in sync with the profiles_avatar_emoji_check
// whitelist in supabase-migration-community-social.sql.
const AVATAR_EMOJI = ['🩺','💉','🏥','🌸','🌟','🍀','🐢','🐧','🦉','🐳','🌿','🫶'];

// ─────────────────────────────────────────────────────────────────────────────
// UserAvatar — generated (letter/emoji) avatar for a community contributor or
// the current user. No uploads → no image moderation. Mirrors ProviderAvatar's
// oklch swatch. The server (get_course_contributors) already resolves
// hue/emoji/isPrivate per the contributor's attribution_visibility, so this
// component just paints:
//   • private ("社群護士") → neutral swatch + person glyph, NO initial
//   • emoji, if the user picked one
//   • else a monogram from displayText, tinted by `hue`
// ─────────────────────────────────────────────────────────────────────────────
function UserAvatar({ size = 32, displayText = '', hue = null, emoji = null, isPrivate = false }) {
  const h = (typeof hue === 'number') ? hue : hashHue(displayText);
  const base = {
    width: size, height: size, borderRadius: Math.round(size * 0.32),
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
  };
  if (isPrivate) {
    return (
      <div className="user-avatar" aria-hidden="true"
           style={{ ...base, background: 'var(--surface-2, oklch(0.93 0.01 250))', color: 'var(--text-muted, oklch(0.55 0.02 250))' }}>
        <Icon name="user" size={Math.round(size * 0.5)} stroke={1.7} />
      </div>
    );
  }
  if (emoji) {
    return (
      <div className="user-avatar" aria-hidden="true"
           style={{ ...base, background: `oklch(0.94 0.03 ${h})`, fontSize: size * 0.52, lineHeight: 1 }}>
        {emoji}
      </div>
    );
  }
  const initial = (displayText || '?').trim().slice(0, 1).toUpperCase();
  return (
    <div className="user-avatar" aria-hidden="true"
         style={{ ...base, background: `oklch(0.92 0.04 ${h})`, color: `oklch(0.36 0.10 ${h})`,
                  fontFamily: 'var(--font-mono)', fontSize: size * 0.42, fontWeight: 600 }}>
      {initial}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Peer-signal chips — what other nurses said about this course.
//
// ‼️ TWO COUNTS, TWO SENTENCES, NEVER ONE NUMBER. The two pools answer
// different questions and were collected under different promises:
//
//   data       helpful / still_accurate — gathered under a button labelled
//              為課程資料背書 ("vouch for this course's DATA"). 51 of the
//              catalogue's 53 reactions are these. Rendering them as "N nurses
//              said it was worth it" would retroactively rewrite what those
//              nurses said: someone who only thought the listing looked
//              complete would be quoted endorsing a course they never attended.
//   experience worth_it / well_organised — only from nurses with a completed
//              record for the course.
//
// Summing them would produce a bigger, more flattering number that means
// nothing. Don't.
//
// Counts arrive already rolled up across the EDITIONS of a repeating programme
// (BLS runs ×21 in the live catalogue), so the visible label stays short and
// the title attribute carries the honest full sentence — including that the
// number spans past runs.
//
// Renders nothing below the display threshold: the server omits those groups
// entirely (it is also the k-anonymity floor), so a card with one rating shows
// no chip rather than exposing a lone nurse's attendance.
//
// Community rows never reach here — both accessors return null for them,
// which is also why this can never double up with the 社群多人支持 SourceBadge.
// ‼️ TWO GRAINS, RENDERED SEPARATELY, NEVER SUMMED. `occ` counts nurses judging
// THIS listing; `ser` counts nurses who attended and judged THE COURSE. Adding
// them would produce a "N 位護士" that describes no group of people that exists.
function PeerSignalChips({ course, lang }) {
  const occ = (typeof window.courseOccurrenceCounts === 'function')
    ? window.courseOccurrenceCounts(course) : null;
  const ser = (typeof window.courseSeriesCounts === 'function')
    ? window.courseSeriesCounts(course) : null;
  // Star reviews, rolled up across the editions of the same programme.
  //
  // ‼️ THE MEAN IS THE SERVER'S TO GIVE. `avg` is null until five nurses have
  // rated the group, because a mean of one renders identically to a mean of two
  // hundred and the first rater would otherwise define a course forever. That
  // rule lives in get_series_review_rollups(), which simply omits the number —
  //
  // ‼️ AND THE COPY FOLLOWS THE SERVER TOO. "包括過往班次" / "including past
  // runs" is a CROSS-OCCURRENCE claim. It may only be shown when the server
  // says `generalizable`, i.e. the evidence actually spans two or more sittings.
  // A single-occurrence series has no past runs in its evidence, and a
  // provisional one has an open boundary on top of that — the numbers are still
  // real, so we narrow the sentence rather than hide them.
  // so there is nothing to "helpfully" show here when it is missing. Below the
  // floor we show the COUNT, which is honest about its own sample size.
  const review = (typeof window.courseReviewSummary === 'function')
    ? window.courseReviewSummary(course) : null;
  if (!occ && !ser && !review) return null;
  const { data = 0, organised = 0 } = occ || {};
  const worth = ser ? ser.worth : 0;
  return (
    <>
      {review && review.avg != null && (
        <span className="cap-pill tone-warning"
              title={lang === 'zh'
                ? (review.generalizable
                    ? `${review.n} 位上過同一課程（包括過往班次）的護士評分`
                    : `${review.n} 位上過這一場的護士評分`)
                : (review.generalizable
                    ? `Rated by ${review.n} nurses who took this course (including past runs)`
                    : `Rated by ${review.n} nurses who attended this session`)}>
          <Icon name="star" size={11} stroke={1.8} color="currentColor" fill="currentColor" />
          {' '}
          {review.avg.toFixed(1)}
          <span style={{ marginLeft: 3, opacity: 0.75, fontVariantNumeric: 'tabular-nums' }}>
            ({review.n})
          </span>
        </span>
      )}
      {/* ‼️ The below-threshold tooltip must follow `generalizable` too. It said
          「包括過往班次」 unconditionally, which is the same cross-occurrence
          claim the star pill above already gates — missed because the two sit in
          different branches of the same component. */}
      {review && review.avg == null && (
        <span className="chip"
              title={lang === 'zh'
                ? (review.generalizable
                    ? `${review.n} 人評價過（包括過往班次）。人數未夠，未顯示平均分。`
                    : `${review.n} 人評價過這一場。人數未夠，未顯示平均分。`)
                : (review.generalizable
                    ? `${review.n} ratings (including past runs) — too few to average yet`
                    : `${review.n} ratings for this session — too few to average yet`)}>
          {lang === 'zh' ? `${review.n} 人評價` : `${review.n} rated`}
        </span>
      )}
      {/* SERIES-owned: worth_it judges the course product, so it may speak across
          runs — but ONLY when the server says the evidence actually spans them.
          `generalizable` is false for a single-occurrence series, and a
          provisional one has an open boundary on top of that. */}
      {worth > 0 && (
        <span className="cap-pill tone-success"
              title={lang === 'zh'
                ? (ser.generalizable
                    ? `${worth} 位上過這個課程（包括過往班次）的護士話值得上`
                    : `${worth} 位上過這一次的護士話值得上`)
                : (ser.generalizable
                    ? `${worth} nurses who took this course (including past runs) said it was worth it`
                    : `${worth} nurses who attended this run said it was worth it`)}>
          <Icon name="thumb-up" size={11} stroke={1.8} />
          {' '}
          {lang === 'zh' ? `${worth} 人話值得上` : `${worth} say worth it`}
        </span>
      )}
      {/* OFFERING-owned: a data vouch says THIS listing's date, venue, fee and
          registration state look right. It transfers to no other sitting — the
          old copy said 「包括過往班次」 here and that was the defect: one nurse
          checking one listing lit up twenty other runs of the same programme. */}
      {data > 0 && (
        <span className="chip"
              title={lang === 'zh'
                ? `${data} 位護士確認這一場的資料準確`
                : `${data} nurses confirmed the details of this session`}>
          {lang === 'zh' ? `${data} 人確認資料` : `${data} confirmed details`}
        </span>
      )}
      {/* OFFERING-owned and attendance-gated: how THAT run was organised. */}
      {organised > 0 && (
        <span className="chip"
              title={lang === 'zh'
                ? `${organised} 位參加過這一場的護士話安排好`
                : `${organised} nurses who attended this session said it was well organised`}>
          {lang === 'zh' ? `${organised} 人話安排好` : `${organised} well organised`}
        </span>
      )}
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Course card (full-width list)
// Uses M.div so it gets whileHover (subtle lift) + whileTap (depress)
// physics — feels alive without a layout shift on hover.
// Keyboard-accessible: tabIndex=0, Enter/Space triggers click.
// ─────────────────────────────────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────────────────────
// Restriction badges — SHARED by CourseCard (Discover's list view) and
// GridCourseCard (its grid view), for the same reason PeerSignalChips is shared:
// a signal that means one thing in the list and another in the grid is worse
// than a slightly tighter tile.
//
// ‼️ THIS SHARING IS LOAD-BEARING. Until 2026-08-10 the grid tile rendered NO
// restriction indicator at all — a 只限公立醫院員工 course was visually
// identical to one anybody can book. That was survivable only because the
// default view hid the ones a nurse could not qualify for. The moment those
// rows are allowed to surface (restrictionVerdict 'unknown' — see data.js) an
// unbadged tile becomes an active lie, so the badge and the loosened filter
// must never be separated again.
//
// Returns null for an unrestricted course, so both call sites can render it
// unconditionally inside their pill row.
function restrictionCardState(course, profile) {
  const restricted = !!course?.isRestricted;
  const elig = (restricted && typeof window.restrictionEligibility === 'function')
    ? window.restrictionEligibility(course, profile) : null;
  // ‼️ ONLY a KNOWN mismatch is desaturated. Desaturation is a verdict about the
  // NURSE — "you probably can't register" — so spending it on 'unknown' tells a
  // signed-out visitor, or anyone whose profile predates the workplace picker,
  // something we have no basis to say. That was the old behaviour and it was the
  // worst of both worlds: greyed out with no words explaining why.
  return { restricted, elig, dimmed: restricted && elig === 'ineligible' };
}

function RestrictionChips({ course, lang, profile }) {
  const { restricted, elig } = restrictionCardState(course, profile);
  if (!restricted) return null;
  // ‼️ ONE PILL, NOT TWO. Until 2026-08-19 the rule pill was always muted and a
  // SECOND chip beside it spelled the verdict out in words (「機構條件符合」 /
  // 「你未必符合」). That is two pills for one fact on the most crowded row in the
  // app, so the verdict now lives ON the rule pill itself:
  //     eligible   → success tone + ✓
  //     ineligible → danger  tone + ✕
  //     unknown    → muted   tone + shield  — says NOTHING about her, because we
  //                  never asked for her workplace (see restrictionVerdict).
  //
  // ‼️ WHAT THE TICK CLAIMS — read this before relabelling the pill.
  // restrictionVerdict matched exactly ONE axis: her workplace against THIS
  // rule. The catalogue carries no register, prerequisite or membership facts to
  // check (eligible_registers is null on every live row). So the tick means "you
  // meet this restriction", NOT 「符合資格」 for the course — which is precisely
  // why the pill keeps the RULE as its label: a tick qualifies the rule it sits
  // on. Never relabel it to a course-level eligibility claim.
  //
  // ‼️ COLOUR IS NEVER THE ONLY CHANNEL. The icon differs per verdict and the
  // whole sentence rides in title + aria-label — otherwise a nurse who does not
  // read colour gets an identical pill for "you're in" and "you're out", the
  // exact failure the words were originally there to prevent.
  const verdict = elig === 'eligible'
    ? { tone: 'success', icon: 'check',
        zh: '你的工作機構符合這項限制', en: 'Your workplace matches this restriction' }
    : elig === 'ineligible'
      ? { tone: 'danger', icon: 'close',
          zh: '你的工作機構未必符合這項限制', en: 'Your workplace may not match this restriction' }
      : { tone: 'muted', icon: 'shield', zh: '', en: '' };
  // ‼️ THE RULE IS THE LABEL. This pill read 「受限」 with the actual rule only in
  // `title=` — a hover tooltip, which a touch device cannot trigger at all. So a
  // nurse had to OPEN a course to discover she could not book it, which is
  // exactly what one wrote in to say ("要按入去才會知道有這要求"). 「受限」 carries
  // no information she can act on; 「只限公立醫院員工」 is the whole point of the
  // badge. Keep the generic word only as the fallback when there is no note.
  const rule = course.restrictionNote
    || (lang === 'zh' ? '受限' : 'Restricted');
  const said = lang === 'zh' ? verdict.zh : verdict.en;
  const full = [course.restrictionNote, said].filter(Boolean).join(' — ');
  return (
    <>
      {/* Comes first so it sits next to the topic pill where users look for the
          "what is this" summary, not buried among the meta chips. */}
      <span className={'cap-pill tone-' + verdict.tone}
        title={full} aria-label={full || rule}
        // The ineligible CARD is desaturated as a whole (saturate(0.55) in
        // CourseCard / GridCourseCard — "you probably can't register"), and a
        // parent filter drains this pill's red along with everything else.
        // Compensate on the one element whose colour is load-bearing.
        style={elig === 'ineligible' ? { filter: 'saturate(1.9)' } : undefined}>
        <Icon name={verdict.icon} size={10} stroke={1.8} />
        {' '}
        <span className="cap-pill-rule">{rule}</span>
      </span>
      {/* Membership restrictions ask a different question from a workplace gate:
          「可申請」 means she can join and then enrol. Kept as its own chip on
          purpose — whether a joinable restriction should count as available at
          all is an OPEN question (docs/course-facts-open-questions.md §2), and
          folding it into the rule pill's tone would read as having decided it. */}
      {elig === 'open' && (
        <span className="cap-pill tone-success">
          {lang === 'zh' ? '可申請' : 'Open to members'}
        </span>
      )}
    </>
  );
}

function CourseCard({ course, lang, onClick, compact = false, profile }) {
  const axes = window.deriveCourseAxes?.(course);
  const ended = typeof isPastCourse === 'function' ? isPastCourse(course) : false;
  // Restriction state — shared with GridCourseCard, see restrictionCardState.
  const { dimmed: restrictedDimmed } = restrictionCardState(course, profile);
  // Single source of truth for "is this open / full / closed / ending soon"
  const info = typeof enrolmentStatusInfo === 'function' ? enrolmentStatusInfo(course) : null;
  // Days until registration deadline — only show if upcoming and reasonable.
  const deadlineDays = (typeof daysUntil === 'function')
    ? daysUntil(course.registrationDeadline || course.raw?.registration_deadline)
    : null;
  const showDeadline = !ended && info?.showCta !== false &&
    deadlineDays != null && deadlineDays >= 0 && deadlineDays <= 14;

  const onKey = (e) => {
    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick && onClick(); }
  };
  return (
    <M.div
      role="button"
      tabIndex={0}
      aria-label={L(course.title, lang)}
      className={'course-card'
        + (ended ? ' is-ended' : '')
        + (restrictedDimmed ? ' is-restricted' : '')}
      onClick={onClick}
      onKeyDown={onKey}
      whileHover={{ y: -2 }}
      whileTap={{ scale: 0.985 }}
      transition={{ duration: 0.18 }}
      // Desaturate only restricted cards the user can't qualify for (the
      // original "you probably can't register" signal); eligible / membership
      // rows are shown deliberately and render at full colour.
      style={restrictedDimmed ? { filter: 'saturate(0.55)' } : undefined}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-start' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="row" style={{ marginBottom: 8 }}>
            <TopicPill topic={course.topic} label={L(course.topicLabel, lang)} />
            {/* Source/verification badge — silent for default (official+verified),
                visible for community-submitted or otherwise non-default rows. */}
            <SourceBadge source={course.source} verification={course.verificationStatus} lang={lang} />
            {/* Members-only / internal indicator + eligibility hint. */}
            <RestrictionChips course={course} lang={lang} profile={profile} />
            {axes?.eventStatus === 'date_tbc' && (
              <span className="cap-pill tone-warning">{L(STR.dateTbc, lang)}</span>
            )}
            {axes?.eventStatus === 'ongoing' && (
              <span className="cap-pill tone-success">{L(STR.ongoingStatus, lang)}</span>
            )}
            {axes?.eventStatus === 'cancelled' && (
              <span className="cap-pill tone-danger">{L(STR.cancelledStatus, lang)}</span>
            )}
            {/* Enrolment-status pill — only when there's something real to say.
                'open' and 'unknown' both render nothing: unknown (no published
                availability) is treated as available, so it looks like a normal
                bookable course rather than carrying a confusing 待確認 badge. Only
                the meaningful warnings (已滿 / 已截止 / 候補 …) get a pill. */}
            {!ended && info && info.kind !== 'open' && info.kind !== 'unknown' && (
              <span className={'cap-pill tone-' + info.tone}>
                {L(info.label, lang)}
              </span>
            )}
            {ended && axes?.eventStatus !== 'cancelled' && (
              <span className="cap-pill tone-muted">
                {(() => {
                  // Mirror the detail page's ended-CTA copy so list pills and
                  // detail CTA stay aligned even in stale-cache scenarios
                  // where an archived row still sits in CoursesCache.
                  const ls = course.lifecycleStatus;
                  if (ls === 'archived') return lang === 'zh' ? '不再提供' : 'No longer offered';
                  if (ls && ls !== 'active') return lang === 'zh' ? '資料不可信' : 'Unavailable';
                  return lang === 'zh' ? '已過期' : 'Ended';
                })()}
              </span>
            )}
            {!ended && course.online && <span className="chip"><Icon name="globe" size={11} stroke={1.8} /> {L(STR.online, lang)}</span>}
            {!ended && course.isFree && <span className="chip">{L(STR.free, lang)}</span>}
            {/* Member saving — only when the provider publishes BOTH tiers and
                they actually differ (deriveFees.memberSaving). A $300 gap is
                what decides whether a nurse joins the society, so it belongs on
                the card, not buried in the detail fee block. */}
            {/* Shift-friendliness — only the POSITIVE signals get a chip.
                "Weekday daytime" and "time not published" stay silent: they are
                the default and the unknown, and a chip for either would be
                noise on most cards. Both remain filterable. */}
            {!ended && course.shiftTag?.weekend === true && (
              <span className="chip">{lang === 'zh' ? '週末' : 'Weekend'}</span>
            )}
            {!ended && course.shiftTag?.weekend !== true && course.shiftTag?.evening === true && (
              <span className="chip">{lang === 'zh' ? '夜間' : 'Evening'}</span>
            )}
            {/* ‼️ 「會員」 WITHOUT A SUBJECT IS A PRICE CLAIM ABOUT THE WRONG
                MEMBERSHIP. This pill used to announce a bare saving for
                「會員」 — no society named — on 95 of the 196 unfinished courses
                (2026-08-15), i.e. nearly every second card, while
                this same app calls its own subscription 「Vital Pro 會員」 in six
                places. The saving belongs to the PROVIDER's society, and the
                reading it invites ("buy Pro, courses get cheaper") is not a
                stretch: it is money, it is on half the feed, and the median gap
                is $200.
                Both tiers are printed instead, so 「會員」 has a subject standing
                next to it. applicableFee (data.js) is the same resolver the route
                planner already prices with, so Discover and the plan can never
                quote her different numbers — and `certain` keeps the epistemic
                rule the whole fee contract rests on: not knowing her membership
                is NOT knowing she is a non-member, so the conservative number is
                never captioned as hers. */}
            {!ended && (() => {
              const m = course.costMember, nm = course.costNonMember;
              if (!(m > 0) || !(nm > 0) || m === nm) return null;
              const fee = window.applicableFee ? window.applicableFee(course, profile) : null;
              const money = (v) => `$${Number(v).toLocaleString('en')}`;
              return fee?.certain && fee.amount === m ? (
                <span className="cap-pill tone-success">
                  {lang === 'zh' ? `你的會員價 ${money(m)}` : `Your member price ${money(m)}`}
                </span>
              ) : (
                <span className="cap-pill tone-success">
                  {lang === 'zh'
                    ? `機構會員 ${money(m)} · 非會員 ${money(nm)}`
                    : `Member ${money(m)} · non-member ${money(nm)}`}
                </span>
              );
            })()}
          </div>
          <h4>{L(course.title, lang)}</h4>
        </div>
        <div style={{ textAlign: 'right', flexShrink: 0, paddingTop: 2 }}>
          <div className="pts">{course.points}</div>
          <div className="pts-label">{L(STR.pts, lang)}</div>
          {axes?.cneStatus === 'confirmed' ? (
            <div className="pts-sub is-confirmed" title={L(STR.cneConfirmed, lang)}>✓ CNE</div>
          ) : course.points != null ? (
            <div className="pts-sub">{L(STR.cneUnverifiedShort, lang)}</div>
          ) : null}
        </div>
      </div>
      <div className="meta" style={{ display: 'flex', gap: 14, alignItems: 'center', flexWrap: 'wrap' }}>
        <span style={{ display: 'inline-flex', gap: 5, alignItems: 'center' }}>
          <Icon name="calendar" size={13} stroke={1.5} />
          {/* Expired courses show the year — "8月6日" alone can't tell last
              year's course from three years ago. Upcoming stay year-less.
              Ongoing multi-session courses show when they run UNTIL, not a
              stale start date that can be weeks in the past — the start-only
              date is what made these look mis-filed in the current tab (the
              進行中 pill above already signals they're live). Falls back to the
              start date when no end date is known. */}
          {axes?.eventStatus === 'ongoing' && course.endDate && course.endDate !== course.date ? (
            lang === 'zh'
              ? `至 ${fmtShort(course.endDate, lang)}`
              : `until ${fmtShort(course.endDate, lang)}`
          ) : axes?.eventStatus === 'date_tbc' && !course.date ? (
            L(STR.dateTbc, lang)
          ) : (
            <>
              {fmtShort(course.date, lang, ended)}
              {course.time ? ' · ' + course.time.split(/[–-]/)[0] : ''}
            </>
          )}
        </span>
        <span style={{ display: 'inline-flex', gap: 5, alignItems: 'center' }}>
          <Icon name="pin" size={13} stroke={1.5} />
          {L(course.providerShort, lang)}
        </span>
        {/* Fee — honours the deriveFees contract (data.js): free is already
            shown as the 免費 chip in the pill row, and an UNKNOWN fee (feeUnknown)
            renders nothing here (never 免費). Only a known positive price shows.
            "起"/"from" when the headline is a tier minimum (no base fee_hkd). */}
        {!ended && !course.isFree && !course.feeUnknown && course.costFrom > 0 && (
          <span style={{ display: 'inline-flex', gap: 5, alignItems: 'center' }}>
            <Icon name="tag" size={13} stroke={1.5} />
            {!course.cost
              ? (lang === 'zh' ? `$${course.costFrom.toLocaleString('en')} 起` : `from $${course.costFrom.toLocaleString('en')}`)
              : `$${course.costFrom.toLocaleString('en')}`}
          </span>
        )}
        {Number(course.sessions) > 1 && (
          <span>{course.sessions} {L(STR.sessionsUnit, lang)}</span>
        )}
        {course.raw?.last_updated && (
          <span>{L(STR.updatedShort, lang)} {fmtShort(String(course.raw.last_updated).slice(0, 10), lang, true)}</span>
        )}
        {!compact && showDeadline && (
          <span className={'deadline-pill' + (deadlineDays <= 3 ? ' urgent' : '')}>
            <Icon name="clock" size={11} stroke={1.7} />
            {deadlineDays === 0
              ? L(STR.closesToday, lang)
              : `${deadlineDays} ${L(deadlineDays === 1 ? STR.dayLeft : STR.daysLeft, lang)}`}
          </span>
        )}
        {/* Peer signal last: it answers "should I pick this one", which is the
            question you ask AFTER date/provider/fee have already qualified the
            course. Silent for the ~80% of cards with no data yet. */}
        <PeerSignalChips course={course} lang={lang} />
      </div>
    </M.div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Mini course card (horizontal scroll)
// ─────────────────────────────────────────────────────────────────────────────
function MiniCourseCard({ course, lang, onClick }) {
  const onKey = (e) => {
    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick && onClick(); }
  };
  return (
    <M.div
      role="button"
      tabIndex={0}
      aria-label={L(course.title, lang)}
      className="mini-card"
      onClick={onClick}
      onKeyDown={onKey}
      whileHover={{ y: -2 }}
      whileTap={{ scale: 0.985 }}
      transition={{ duration: 0.18 }}
    >
      <div className="mc-head">
        <TopicPill topic={course.topic} label={L(course.topicLabel, lang)} />
        <span className="mc-date">{fmtShort(course.date, lang)}</span>
      </div>
      <h5 title={L(course.title, lang)}>{L(course.title, lang)}</h5>
      <div className="mc-foot">
        <span className="mc-provider">
          <Icon name="pin" size={11} stroke={1.6} />
          <span className="mc-provider-name">{L(course.providerShort, lang)}</span>
        </span>
        <div className="mc-pts">
          <span className="serif mc-pts-num">{course.points}</span>
          <span className="mono muted-2 mc-pts-label">{L(STR.pts, lang)}</span>
        </div>
      </div>
    </M.div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Segmented progress ring (categories) — used in dashboard wallet
// ─────────────────────────────────────────────────────────────────────────────
function ProgressRing({ size = 132, stroke = 9, segments }) {
  // segments: [{value, color}], sum should not exceed total
  const total = segments.reduce((s, x) => s + x.total, 0);
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  let offset = 0;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ transform: 'rotate(-90deg)' }}>
      <circle cx={size/2} cy={size/2} r={r}
              stroke="var(--bg-2)" strokeWidth={stroke} fill="none" />
      {segments.map((seg, i) => {
        const dash = (seg.value / total) * c;
        const dashOffset = c - offset - dash;
        const el = (
          <circle key={i} cx={size/2} cy={size/2} r={r}
                  stroke={seg.color}
                  strokeWidth={stroke}
                  fill="none"
                  strokeLinecap="round"
                  strokeDasharray={`${dash} ${c}`}
                  strokeDashoffset={-offset}
                  style={{ transition: 'stroke-dashoffset .6s, stroke-dasharray .6s' }}
          />
        );
        offset += dash + 4; // 4px gap
        return el;
      })}
    </svg>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Tab Bar (5 tabs with center FAB)
// • Every tab is M.button so it gets physical tap feedback.
// • The center FAB scales further (deeper press feels heavier — matches
//   the visual weight of the dark circle).
// • Active state's icon stroke shift is animated via the surrounding
//   color transition we set in CSS.
// ─────────────────────────────────────────────────────────────────────────────
function TabBar({ active, onChange, lang, onLog, recordBadge = 0 }) {
  const tab = (id, icon, labelKey, badge) => {
    const showBadge = Number.isFinite(badge) && badge > 0;
    const badgeLabel = showBadge ? (badge > 99 ? '99+' : String(badge)) : null;
    return (
      <M.button
        key={id}
        className={'tab' + (active === id ? ' on' : '')}
        onClick={() => onChange(id)}
        whileTap={{ scale: 0.92 }}
        transition={{ duration: 0.14, ease: window.VITAL_SPRING ? window.VITAL_SPRING.bounce : undefined }}
        aria-label={
          showBadge
            ? `${L(STR[labelKey], lang)} (${badgeLabel})`
            : L(STR[labelKey], lang)
        }
        aria-current={active === id ? 'page' : undefined}
      >
        <span className="tab-icon-wrap">
          <Icon name={icon} size={22} stroke={active === id ? 1.9 : 1.6} />
          {showBadge && (
            <span className="tab-badge" aria-hidden="true">{badgeLabel}</span>
          )}
        </span>
        <span>{L(STR[labelKey], lang)}</span>
      </M.button>
    );
  };
  return (
    <div className="tab-bar">
      {tab('home', 'home', 'home')}
      {tab('discover', 'compass', 'discover')}
      <M.button
        className="tab fab"
        onClick={onLog}
        aria-label={L(STR.logCourse, lang) || 'Log course'}
        whileTap={{ scale: 0.88 }}
        whileHover={{ y: -1 }}
        transition={{ duration: 0.16, ease: window.VITAL_SPRING ? window.VITAL_SPRING.bounce : undefined }}
      >
        <div className="fab-circle">
          <Icon name="plus" size={22} stroke={2.2} />
        </div>
      </M.button>
      {tab('record', 'book', 'myRecord', recordBadge)}
      {tab('profile', 'user', 'profile')}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Bottom sheet (mobile) / centered modal (≥768)
//
// Exit-animation strategy:
//   The parent typically renders us conditionally — when it sets us to
//   null we'd unmount instantly, killing any exit animation. To avoid
//   forcing the parent to wrap us in <AnimatePresence>, the Sheet
//   intercepts onClose: on user-initiated close (backdrop / x button /
//   Esc) it sets `closing=true`, lets the CSS exit animation play
//   (~240ms), THEN calls the supplied onClose. This means every code
//   path that closes the sheet flows through the same animation, with
//   zero changes upstream.
//
//   prefers-reduced-motion users skip the wait — the CSS animation
//   collapses to ~0s via the global @media block in styles.css.
// ─────────────────────────────────────────────────────────────────────────────
function Sheet({ open, onClose, children, title }) {
  const [closing, setClosing] = useState(false);
  // `useReducedMotion` is defined in motion-kit.jsx, which loads before
  // components.jsx in both dev (index.html) and prod (src/entry.js). A
  // ternary around a hook call violates Rules of Hooks — call it directly.
  const reduced = useReducedMotion();
  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  const playClose = useCallback(() => {
    if (closing) return;
    if (reduced) { onClose && onClose(); return; }
    setClosing(true);
    setTimeout(() => {
      setClosing(false);
      closeRef.current && closeRef.current();
    }, 240);
  }, [closing, reduced, onClose]);

  // Escape key dismisses
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') playClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, playClose]);

  if (!open) return null;
  return (
    <Fragment>
      <div
        className={'sheet-backdrop' + (closing ? ' is-closing' : '')}
        onClick={playClose}
        aria-hidden="true"
      />
      <div
        className={'sheet' + (closing ? ' is-closing' : '')}
        role="dialog"
        aria-modal="true"
        aria-label={title || undefined}
      >
        <div className="sheet-handle" aria-hidden="true" />
        {title && (
          <div style={{
            padding: '14px 20px 10px',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            flexShrink: 0,
          }}>
            <div className="h-section">{title}</div>
            <M.button
              className="icon-btn"
              onClick={playClose}
              style={{ width: 32, height: 32 }}
              whileTap={{ scale: 0.9 }}
              aria-label="Close"
            >
              <Icon name="close" size={16} />
            </M.button>
          </div>
        )}
        <div style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden' }}>
          {children}
        </div>
      </div>
    </Fragment>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// WHEEL DATE PICKER  (year · month · day)
// ═════════════════════════════════════════════════════════════════════════════
// Drop-in replacement for every <input type="date"> in the app — an iOS-style
// bottom-sheet wheel with momentum snap, a subtle tick sound, and native
// selection haptics on each detent. Value in/out is the SAME ISO YYYY-MM-DD
// string as the native input, so call sites only swap the element + change
// `onChange={e => f(e.target.value)}` to `onChange={f}`.
//
// ⚠️  Do NOT reintroduce a raw <input type="date"> anywhere — it renders as the
//     cramped iOS system popover and loses the tick/haptic feedback. The
//     eslint `no-restricted-syntax` rule (eslint.config.mjs) bans it so it
//     can't creep back; route new date fields through <DateField> instead.
//
// Feedback is best-effort and self-disabling off the happy path: the
// AudioContext is created LAZILY on first use (never at module load — the
// build.mjs VM smoke test has no AudioContext), and haptics route through
// window.CapacitorHaptics, which only exists in the native bundle
// (src/capacitor-bridge.js) so dev/web silently no-ops.
// ─────────────────────────────────────────────────────────────────────────────
const DP_MONTHS_EN = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const DP_ITEM_H = 40;     // px per wheel row — MUST match .wheel-item / .wheel-pad in styles.css
const DP_VISIBLE = 5;     // odd, so exactly one row sits under the centre highlight

function dpPad2(n) { return String(n).padStart(2, '0'); }
function dpDaysInMonth(y, m) { return new Date(y, m, 0).getDate(); }          // m is 1-12
function dpRangeItems(lo, hi, label) {
  const out = [];
  for (let v = lo; v <= hi; v++) out.push({ v, label: label(v) });
  return out;
}
function dpParse(iso) {
  if (iso && /^\d{4}-\d{2}-\d{2}$/.test(iso)) {
    const [y, m, d] = iso.split('-').map(Number);
    return { y, m, d };
  }
  const t = new Date();
  return { y: t.getFullYear(), m: t.getMonth() + 1, d: t.getDate() };
}
// Clamp a {y,m,d} to the year range, the month's real day-count (leap years via
// dpDaysInMonth), then to the whole-date min/max — so start≤end constraints and
// Feb-29 edges resolve to a valid date.
function dpClamp(s, minP, maxP, yLo, yHi) {
  let y = Math.max(yLo, Math.min(yHi, s.y));
  const m = Math.max(1, Math.min(12, s.m));
  let d = Math.max(1, Math.min(dpDaysInMonth(y, m), s.d));
  const cmp = (a, b) => (a.y - b.y) || (a.m - b.m) || (a.d - b.d);
  let out = { y, m, d };
  if (minP && cmp(out, minP) < 0) out = { ...minP };
  if (maxP && cmp(out, maxP) > 0) out = { ...maxP };
  return out;
}

let _dpAudioCtx = null;
// Create/resume the shared AudioContext. MUST be called from a real user
// gesture (touchstart / the trigger tap) — iOS won't let a `scroll` handler
// unlock audio, so we arm it here and the subsequent scroll ticks can play.
function dpEnsureAudio() {
  try {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return null;
    if (!_dpAudioCtx) _dpAudioCtx = new AC();
    if (_dpAudioCtx.state === 'suspended') _dpAudioCtx.resume();
    return _dpAudioCtx;
  } catch (_) { return null; }
}
function dpTick() {
  try {
    const ctx = dpEnsureAudio();
    if (!ctx) return;
    const now = ctx.currentTime;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'square';
    osc.frequency.value = 1650;
    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.exponentialRampToValueAtTime(0.05, now + 0.001);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.024);
    osc.connect(gain).connect(ctx.destination);
    osc.start(now);
    osc.stop(now + 0.03);
  } catch (_) { /* audio unavailable — silent */ }
}
function dpHapticsOn() {
  try { return !!(globalThis.Capacitor?.isNativePlatform?.() && window.CapacitorHaptics); }
  catch (_) { return false; }
}
function dpHapticStart()  { if (dpHapticsOn()) { try { window.CapacitorHaptics.selectionStart(); } catch (_) {} } }
function dpHapticChange() { dpTick(); if (dpHapticsOn()) { try { window.CapacitorHaptics.selectionChanged(); } catch (_) {} } }
function dpHapticEnd()    { if (dpHapticsOn()) { try { window.CapacitorHaptics.selectionEnd(); } catch (_) {} } }

// One scrollable wheel. Uncontrolled during interaction: it owns its scroll and
// only reports the settled value up. It re-scrolls to `value` programmatically
// ONLY when `value` differs from what it last emitted (e.g. an external day-clamp
// when the month changes) — never in reaction to the user's own scroll, which
// would fight the momentum.
function WheelColumn({ items, value, onChange, ariaLabel }) {
  const ref = useRef(null);
  const rafRef = useRef(0);
  const settleRef = useRef(0);
  const progRef = useRef(false);        // a programmatic scroll is in flight
  const progTRef = useRef(0);
  const touchingRef = useRef(false);
  const lastRef = useRef(value);        // last value THIS column emitted / synced

  const idxOf = (v) => { const i = items.findIndex(it => it.v === v); return i < 0 ? 0 : i; };
  const centerIdx = (el) => Math.max(0, Math.min(items.length - 1, Math.round(el.scrollTop / DP_ITEM_H)));

  const scrollToIdx = (i, smooth) => {
    const el = ref.current; if (!el) return;
    progRef.current = true;
    el.scrollTo({ top: i * DP_ITEM_H, behavior: smooth ? 'smooth' : 'auto' });
    clearTimeout(progTRef.current);
    progTRef.current = setTimeout(() => { progRef.current = false; }, smooth ? 280 : 60);
  };

  // Mount: position on the initial value, no animation, no feedback.
  useEffect(() => { scrollToIdx(idxOf(value), false); lastRef.current = value; }, []);

  // External value change (day clamp / min-max snap): scroll only if we didn't emit it.
  useEffect(() => {
    if (value === lastRef.current) return;
    lastRef.current = value;
    scrollToIdx(idxOf(value), true);
  }, [value]);

  const emit = (v) => { lastRef.current = v; dpHapticChange(); onChange(v); };

  const onScroll = () => {
    const el = ref.current; if (!el) return;
    if (!rafRef.current) {
      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = 0;
        if (progRef.current) return;
        const v = items[centerIdx(el)]?.v;
        if (v != null && v !== lastRef.current) emit(v);
      });
    }
    clearTimeout(settleRef.current);
    settleRef.current = setTimeout(() => {
      const el2 = ref.current; if (!el2) return;
      if (touchingRef.current || progRef.current) return;
      const i = centerIdx(el2);
      if (Math.abs(el2.scrollTop - i * DP_ITEM_H) > 0.5) scrollToIdx(i, true);
      const v = items[i]?.v;
      if (v != null && v !== lastRef.current) emit(v);
      dpHapticEnd();
    }, 130);
  };

  const step = (delta) => {
    const i = Math.max(0, Math.min(items.length - 1, idxOf(lastRef.current) + delta));
    const v = items[i]?.v;
    if (v != null && v !== lastRef.current) { emit(v); scrollToIdx(i, true); }
  };

  return (
    <div className="wheel-col" ref={ref} onScroll={onScroll} role="listbox" aria-label={ariaLabel} tabIndex={0}
      onTouchStart={() => { touchingRef.current = true; dpEnsureAudio(); dpHapticStart(); }}
      onTouchEnd={() => { touchingRef.current = false; }}
      onKeyDown={(e) => {
        if (e.key === 'ArrowDown') { e.preventDefault(); step(1); }
        else if (e.key === 'ArrowUp') { e.preventDefault(); step(-1); }
      }}>
      <div className="wheel-pad" aria-hidden="true" />
      {items.map((it) => (
        <div key={it.v} className={'wheel-item' + (it.v === value ? ' is-active' : '')}
          role="option" aria-selected={it.v === value}
          onClick={() => { if (it.v !== lastRef.current) emit(it.v); scrollToIdx(idxOf(it.v), true); }}>
          {it.label}
        </div>
      ))}
      <div className="wheel-pad" aria-hidden="true" />
    </div>
  );
}

// The bottom-sheet modal. Holds the selected Y/M/D locally (seeded from `value`
// on open); commits to onCommit only on 完成/Done, so the parent form doesn't
// re-render on every scroll. 取消/Esc/backdrop leave the field unchanged.
function WheelDatePicker({ open, value, min, max, lang, clearable, onCommit, onClose }) {
  const reduced = useReducedMotion();
  const [closing, setClosing] = useState(false);
  const panelRef = useRef(null);

  const isZh = lang === 'zh';
  const minP = min ? dpParse(min) : null;
  const maxP = max ? dpParse(max) : null;
  const nowY = new Date().getFullYear();
  const yLo = minP ? minP.y : nowY - 40;
  const yHi = maxP ? maxP.y : nowY + 5;

  const [sel, setSel] = useState(() => dpClamp(dpParse(value), minP, maxP, yLo, yHi));

  // Re-seed each time the sheet (re)opens with a possibly-new value.
  useEffect(() => {
    if (open) { setClosing(false); setSel(dpClamp(dpParse(value), minP, maxP, yLo, yHi)); }
  }, [open, value]);

  useEffect(() => { if (open && panelRef.current) panelRef.current.focus(); }, [open]);

  const doClose = useCallback((commitIso) => {
    const finish = () => { setClosing(false); if (commitIso === undefined) onClose(); else onCommit(commitIso); };
    if (reduced) { finish(); return; }
    setClosing(true);
    setTimeout(finish, 240);
  }, [reduced, onCommit, onClose]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') doClose(undefined); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, doClose]);

  if (!open) return null;

  const years = dpRangeItems(yLo, yHi, (y) => String(y));
  const months = dpRangeItems(1, 12, (m) => isZh ? `${m}月` : DP_MONTHS_EN[m - 1]);
  const days = dpRangeItems(1, dpDaysInMonth(sel.y, sel.m), (d) => isZh ? `${d}日` : String(d));
  const iso = `${sel.y}-${dpPad2(sel.m)}-${dpPad2(sel.d)}`;

  const setPart = (part) => (v) => setSel((s) => dpClamp({ ...s, [part]: v }, minP, maxP, yLo, yHi));

  // Portal to <body> so the fixed backdrop escapes whatever positioned/transformed
  // subtree the DateField sits in (cards, sheets, register-dialog) and stacks at
  // viewport level above every app layer — same rationale as the discover-filters
  // sheet. Theme tokens still resolve: [data-theme] lives on <html>, so <body>
  // descendants inherit the CSS variables.
  return ReactDOM.createPortal(
    <Fragment>
      <div className={'wheelpick-backdrop' + (closing ? ' is-closing' : '')} onClick={() => doClose(undefined)} aria-hidden="true" />
      <div className={'wheelpick' + (closing ? ' is-closing' : '')} role="dialog" aria-modal="true"
        aria-label={isZh ? '選擇日期' : 'Select date'} ref={panelRef} tabIndex={-1}>
        <div className="wheelpick-bar">
          <button type="button" className="wheelpick-btn" onClick={() => doClose(undefined)}>{isZh ? '取消' : 'Cancel'}</button>
          <div className="wheelpick-title">{fmtDate(iso, lang)}</div>
          <button type="button" className="wheelpick-btn is-primary" onClick={() => doClose(iso)}>{isZh ? '完成' : 'Done'}</button>
        </div>
        <div className="wheelpick-wheels">
          <div className="wheelpick-highlight" aria-hidden="true" />
          <WheelColumn items={years} value={sel.y} onChange={setPart('y')} ariaLabel={isZh ? '年' : 'Year'} />
          <WheelColumn items={months} value={sel.m} onChange={setPart('m')} ariaLabel={isZh ? '月' : 'Month'} />
          <WheelColumn items={days} value={sel.d} onChange={setPart('d')} ariaLabel={isZh ? '日' : 'Day'} />
        </div>
        {clearable && (
          <button type="button" className="wheelpick-clear" onClick={() => doClose('')}>{isZh ? '清除日期' : 'Clear date'}</button>
        )}
      </div>
    </Fragment>,
    document.body
  );
}

// The drop-in trigger. Renders a real <button> styled like the field it replaces
// (pass className: 'field-input' or 'auth-input'), showing the formatted date or
// a muted placeholder, and opens the wheel sheet on tap/Enter/Space.
function DateField({ value, onChange, min, max, lang = 'en', placeholder, className = 'field-input', style, compact, clearable = false }) {
  const [open, setOpen] = useState(false);
  const btnRef = useRef(null);
  const isZh = lang === 'zh';
  const label = value ? fmtDate(value, lang) : '';
  const ph = placeholder || (isZh ? '選擇日期' : 'Select date');
  const refocus = () => requestAnimationFrame(() => btnRef.current && btnRef.current.focus());
  return (
    <Fragment>
      <button type="button" ref={btnRef} style={style}
        className={'date-field ' + className + (compact ? ' is-compact' : '')}
        onClick={() => { dpEnsureAudio(); setOpen(true); }} aria-haspopup="dialog"
        aria-label={label ? (isZh ? `日期：${label}` : `Date: ${label}`) : ph}>
        <span className={label ? '' : 'date-field-ph'}>{label || ph}</span>
        <Icon name="calendar" size={compact ? 14 : 16} color="var(--fg-3)" />
      </button>
      <WheelDatePicker open={open} value={value} min={min} max={max} lang={lang} clearable={clearable}
        onCommit={(v) => { setOpen(false); onChange(v); refocus(); }}
        onClose={() => { setOpen(false); refocus(); }} />
    </Fragment>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// WHEEL TIME PICKER  (hour · minute, and the 4-wheel start→end range)
// ═════════════════════════════════════════════════════════════════════════════
// The same machinery as WheelDatePicker above — WheelColumn, the tick + haptic
// detents, the body portal, the 5-row × 40px geometry — with clock wheels
// instead of calendar ones. Two call shapes:
//
//   <TimeField>       2 wheels, value "HH:MM"        — one session's start time
//   <TimeRangeField>  4 wheels, value "HH:MM-HH:MM"  — a course's class time
//
// ⚠️  Do NOT reintroduce a raw <input type="time"> — it renders as the cramped
//     iOS system popover and loses the tick/haptic feedback, exactly like
//     <input type="date">. The eslint `no-restricted-syntax` rule in
//     eslint.config.mjs bans the element so it can't creep back.
//
// ‼️ THE RANGE'S OUT-FORM IS A CONTRACT, not a display choice. `HH:MM-HH:MM`
//    (24-hour, ASCII hyphen, no spaces) is the literal string that lands in
//    cne_courses.time_text, and two other places already agree on it:
//    cleanTimeRange() in supabase/functions/extract-certificate/index.ts
//    normalises the model's answer to it, and the extraction prompt asks for it
//    by name. Change one, change all three.
const TP_MIN_STEP = 5;    // minute detent — 00, 05, … 55

function tpTo24(h, meridiem) {
  const ap = (meridiem || '').toLowerCase().replace(/\./g, '');
  if (ap === 'pm' && h < 12) return h + 12;
  if (ap === 'am' && h === 12) return 0;
  return h;
}
// The first `count` clock readings in a free-text time. A BARE number only
// counts when it carries am/pm, so the "1" in "Day 1 08:30 - 18:00" is not read
// as 01:00 — same rule as _firstStartHour() in data.js, which measured the
// formats the live catalogue actually carries ("7:00pm - 9:00pm", "9:00 -
// 13:15", "18:30-21:30", "Day 1 08:30 - 18:00; Day 2 08:30 - 13:00").
function tpReadClocks(text, count) {
  const out = [];
  if (!text || typeof text !== 'string') return out;
  const re = /(\d{1,2}):(\d{2})\s*(a\.?m\.?|p\.?m\.?)?|(\d{1,2})\s*(a\.?m\.?|p\.?m\.?)/gi;
  let m;
  while (out.length < count && (m = re.exec(text)) !== null) {
    const h = Number(m[1] != null ? m[1] : m[4]);
    const mi = m[2] != null ? Number(m[2]) : 0;
    if (!Number.isFinite(h) || h > 23 || mi > 59) continue;
    out.push({ h: tpTo24(h, m[3] || m[5]), m: mi });
  }
  return out;
}
function tpParseOne(text) { const c = tpReadClocks(text, 1); return c.length ? c[0] : null; }
function tpParseRange(text) {
  const c = tpReadClocks(text, 2);
  return c.length === 2 ? { s: c[0], e: c[1] } : null;
}
function tpFmt(t) { return `${dpPad2(t.h)}:${dpPad2(t.m)}`; }
function tpFmtRange(sel) { return sel.e ? `${tpFmt(sel.s)}-${tpFmt(sel.e)}` : tpFmt(sel.s); }
function tpMins(t) { return t.h * 60 + t.m; }

// Minute wheel: the 5-minute detents PLUS any off-step minute the INCOMING
// value carried (13:15 and 08:47 both exist in the live catalogue), so opening
// the picker on legacy data can never silently round it away. `keep` is frozen
// for the lifetime of one open — a list that grew and shrank mid-scroll would
// shift every row sitting under the highlight.
function tpMinuteItems(keep) {
  const vals = [];
  for (let m = 0; m < 60; m += TP_MIN_STEP) vals.push(m);
  for (const k of keep || []) {
    if (Number.isFinite(k) && k >= 0 && k <= 59 && k % TP_MIN_STEP !== 0 && !vals.includes(k)) vals.push(k);
  }
  vals.sort((a, b) => a - b);
  return vals.map((v) => ({ v, label: dpPad2(v) }));
}
// The canonical STORED form of an incoming value, or null when the wheels
// cannot round-trip it. Two callers depend on it, both for the same reason —
// the four wheels read only the first one or two clocks in the string, so
// "Day 1 08:30 - 18:00; Day 2 08:30 - 13:00" would come back as 08:30-18:00 and
// throw Day 2 away. Anything that is not already canonical is therefore shown
// VERBATIM in the field and warned about before it is replaced.
function tpCanonical(value, range) {
  const raw = String(value == null ? '' : value).trim();
  if (!raw) return null;
  if (range) { const p = tpParseRange(raw); return p ? `${tpFmt(p.s)}-${tpFmt(p.e)}` : null; }
  const p = tpParseOne(raw);
  return p ? tpFmt(p) : null;
}

// Seed the wheels. 19:00–21:00 is only a RESTING POSITION for an empty field,
// not a claim about the catalogue: nothing is written until the user taps 完成.
function tpSeed(value, range) {
  if (range) {
    const p = tpParseRange(value);
    return p ? { s: { ...p.s }, e: { ...p.e } } : { s: { h: 19, m: 0 }, e: { h: 21, m: 0 } };
  }
  const p = tpParseOne(value);
  return { s: p ? { ...p } : { h: 19, m: 0 }, e: null };
}

// The bottom-sheet modal. Holds the selection locally (seeded from `value` on
// open) and commits only on 完成/Done, so the parent form doesn't re-render on
// every scroll. 取消/Esc/backdrop leave the field unchanged.
function WheelTimePicker({ open, value, lang, range, clearable, onCommit, onClose }) {
  const reduced = useReducedMotion();
  const [closing, setClosing] = useState(false);
  const panelRef = useRef(null);
  const isZh = lang === 'zh';

  const [sel, setSel] = useState(() => tpSeed(value, range));
  const [keepMins, setKeepMins] = useState(() => {
    const x = tpSeed(value, range);
    return [x.s.m, x.e ? x.e.m : null].filter((v) => v != null);
  });

  // Re-seed each time the sheet (re)opens with a possibly-new value.
  useEffect(() => {
    if (!open) return;
    setClosing(false);
    const x = tpSeed(value, range);
    setSel(x);
    setKeepMins([x.s.m, x.e ? x.e.m : null].filter((v) => v != null));
  }, [open, value, range]);

  useEffect(() => { if (open && panelRef.current) panelRef.current.focus(); }, [open]);

  const doClose = useCallback((commitText) => {
    const finish = () => { setClosing(false); if (commitText === undefined) onClose(); else onCommit(commitText); };
    if (reduced) { finish(); return; }
    setClosing(true);
    setTimeout(finish, 240);
  }, [reduced, onCommit, onClose]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') doClose(undefined); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, doClose]);

  if (!open) return null;

  const hours = dpRangeItems(0, 23, (h) => dpPad2(h));
  const minutes = tpMinuteItems(keepMins);
  const out = tpFmtRange(sel);
  // An end at or before the start is not a class time. Say so and hold Done
  // shut rather than silently clamping a wheel the user is still dragging —
  // a wheel that snaps back while your finger is on it reads as a broken
  // control, and there is no correct value to snap to anyway.
  const invalid = !!(range && sel.e && tpMins(sel.e) <= tpMins(sel.s));
  // The incoming text is not what these wheels would have written — prose, a
  // multi-day time ("Day 1 08:30 - 18:00; Day 2 …"), or just an un-normalised
  // "7:00pm - 9:00pm". Committing REPLACES it, so show the original and the
  // replacement side by side rather than letting a tap eat a day.
  const raw = String(value == null ? '' : value).trim();
  const lossy = !!raw && raw !== tpCanonical(value, range);

  const setPart = (side, part) => (v) => setSel((s) => ({ ...s, [side]: { ...s[side], [part]: v } }));

  return ReactDOM.createPortal(
    <Fragment>
      <div className={'wheelpick-backdrop' + (closing ? ' is-closing' : '')} onClick={() => doClose(undefined)} aria-hidden="true" />
      <div className={'wheelpick' + (closing ? ' is-closing' : '')} role="dialog" aria-modal="true"
        aria-label={isZh ? '選擇時間' : 'Select time'} ref={panelRef} tabIndex={-1}>
        <div className="wheelpick-bar">
          <button type="button" className="wheelpick-btn" onClick={() => doClose(undefined)}>{isZh ? '取消' : 'Cancel'}</button>
          <div className={'wheelpick-title' + (invalid ? ' is-invalid' : '')}>
            {invalid
              ? (isZh ? '結束時間要遲過開始時間' : 'End must be after start')
              : (range ? `${tpFmt(sel.s)} – ${tpFmt(sel.e)}` : tpFmt(sel.s))}
          </div>
          <button type="button" className="wheelpick-btn is-primary" disabled={invalid}
            style={invalid ? { opacity: 0.4 } : undefined}
            onClick={() => { if (!invalid) doClose(out); }}>{isZh ? '完成' : 'Done'}</button>
        </div>
        {range && (
          <div className="wheelpick-heads" aria-hidden="true">
            <div className="wheelpick-head">{isZh ? '開始' : 'Start'}</div>
            <div className="wheelpick-head">{isZh ? '結束' : 'End'}</div>
          </div>
        )}
        <div className="wheelpick-wheels">
          <div className="wheelpick-highlight" aria-hidden="true" />
          <WheelColumn items={hours} value={sel.s.h} onChange={setPart('s', 'h')}
            ariaLabel={isZh ? (range ? '開始時' : '時') : (range ? 'Start hour' : 'Hour')} />
          <WheelColumn items={minutes} value={sel.s.m} onChange={setPart('s', 'm')}
            ariaLabel={isZh ? (range ? '開始分' : '分') : (range ? 'Start minute' : 'Minute')} />
          {range && sel.e && (
            <Fragment>
              <WheelColumn items={hours} value={sel.e.h} onChange={setPart('e', 'h')}
                ariaLabel={isZh ? '結束時' : 'End hour'} />
              <WheelColumn items={minutes} value={sel.e.m} onChange={setPart('e', 'm')}
                ariaLabel={isZh ? '結束分' : 'End minute'} />
            </Fragment>
          )}
        </div>
        {lossy && (
          <div className="wheelpick-note">
            {isZh
              ? `原本填咗「${raw}」。按「完成」會改成 ${out}。`
              : `Done replaces the current value “${raw}” with ${out}.`}
          </div>
        )}
        {clearable && (
          <button type="button" className="wheelpick-clear" onClick={() => doClose('')}>{isZh ? '清除時間' : 'Clear time'}</button>
        )}
      </div>
    </Fragment>,
    document.body
  );
}

// The drop-in triggers, styled like the field they replace (pass className:
// 'field-input'). An unparseable legacy value is shown VERBATIM rather than
// blanked, so a multi-day time_text stays visible until someone chooses to
// replace it.
function TimeRangeField({ value, onChange, lang = 'en', placeholder, className = 'field-input', style, compact, clearable = true }) {
  const [open, setOpen] = useState(false);
  const btnRef = useRef(null);
  const isZh = lang === 'zh';
  const raw = String(value == null ? '' : value).trim();
  const parsed = raw && raw === tpCanonical(value, true) ? tpParseRange(raw) : null;
  const label = parsed ? `${tpFmt(parsed.s)} – ${tpFmt(parsed.e)}` : raw;
  const ph = placeholder || (isZh ? '選擇時間' : 'Select time');
  const refocus = () => requestAnimationFrame(() => btnRef.current && btnRef.current.focus());
  return (
    <Fragment>
      <button type="button" ref={btnRef} style={style}
        className={'time-field ' + className + (compact ? ' is-compact' : '')}
        onClick={() => { dpEnsureAudio(); setOpen(true); }} aria-haspopup="dialog"
        aria-label={label ? (isZh ? `時間:${label}` : `Time: ${label}`) : ph}>
        <span className={label ? '' : 'time-field-ph'}>{label || ph}</span>
        <Icon name="clock" size={compact ? 14 : 16} color="var(--fg-3)" />
      </button>
      <WheelTimePicker open={open} value={value} lang={lang} range clearable={clearable}
        onCommit={(v) => { setOpen(false); onChange(v); refocus(); }}
        onClose={() => { setOpen(false); refocus(); }} />
    </Fragment>
  );
}

function TimeField({ value, onChange, lang = 'en', placeholder, className = 'field-input', style, compact, clearable = true }) {
  const [open, setOpen] = useState(false);
  const btnRef = useRef(null);
  const isZh = lang === 'zh';
  const label = String(value == null ? '' : value).trim();
  const ph = placeholder || (isZh ? '時間' : 'Time');
  const refocus = () => requestAnimationFrame(() => btnRef.current && btnRef.current.focus());
  return (
    <Fragment>
      <button type="button" ref={btnRef} style={style}
        className={'time-field ' + className + (compact ? ' is-compact' : '')}
        onClick={() => { dpEnsureAudio(); setOpen(true); }} aria-haspopup="dialog"
        aria-label={label ? (isZh ? `時間:${label}` : `Time: ${label}`) : ph}>
        <span className={label ? '' : 'time-field-ph'}>{label || ph}</span>
        <Icon name="clock" size={compact ? 13 : 16} color="var(--fg-3)" />
      </button>
      <WheelTimePicker open={open} value={value} lang={lang} clearable={clearable}
        onCommit={(v) => { setOpen(false); onChange(v); refocus(); }}
        onClose={() => { setOpen(false); refocus(); }} />
    </Fragment>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Header (replaces nav bar — light, custom)
// ─────────────────────────────────────────────────────────────────────────────
function ScreenHeader({ title, eyebrow, actions, onBack, lang, sticky, inlineBack = false }) {
  if (inlineBack && onBack) {
    return (
      <div className={'top top-inline' + (sticky ? ' sticky' : '')}>
        <button className="icon-btn" onClick={onBack}
          aria-label={lang === 'zh' ? '返回' : 'Back'}>
          <Icon name="chev-l" size={18} />
        </button>
        <div className="top-inline-title">
          {eyebrow && <div className="eyebrow">{eyebrow}</div>}
          <div className="h-screen">{title}</div>
        </div>
        {actions && <div className="top-actions">{actions}</div>}
      </div>
    );
  }
  return (
    <div className={'top' + (sticky ? ' sticky' : '')}>
      <div style={{ flex: 1, minWidth: 0 }}>
        {onBack && (
          <button className="icon-btn" style={{ marginBottom: 12 }} onClick={onBack}>
            <Icon name="chev-l" size={18} />
          </button>
        )}
        {eyebrow && <div className="eyebrow" style={{ marginBottom: 6 }}>{eyebrow}</div>}
        <div className="h-screen">{title}</div>
      </div>
      {actions && <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>{actions}</div>}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// MembershipPicker — 專業會籍
//
// ‼️ WHY THIS REPLACED A FREE-TEXT LINE. The old field asked her to type her
// societies and then compared what she typed to each provider's full ENGLISH
// name. "HKACCN" and "香港護士協會" — the field's own placeholder examples —
// both failed to match anything, which is the real reason every route on the
// Home card was priced "從 HK$X 起" instead of at her actual rate. See the
// header above membershipMatchesCourse in data.js for the measurement.
//
// ‼️ THE CHIPS ARE A PROMISE, NOT A DIRECTORY. Every option comes from
// membershipProviderOptions(), i.e. providers whose catalogue rows actually
// publish DIFFERENT member and non-member prices — so ticking one is
// guaranteed to change a number. It is NOT a list of Hong Kong nursing
// societies and must never be presented as one.
//
// ‼️ THE FREE-TEXT BOX STAYS, and that is deliberate. The original objection to
// a picker was sound: there is no closed set of societies, and a picker that
// omits hers is worse than a box. Both are kept — the chips carry the ones we
// can price, the box carries everything else, and the copy under it says
// plainly that a society outside the list changes no cost. Do not "simplify"
// this by deleting the box.
//
// ‼️ NOTHING STORED IS EVER SILENTLY DROPPED. A provider can stop publishing a
// split price for a season, and its chip then disappears from the options; that
// stored code falls into the free-text box instead, where she can still see and
// edit it. Matching does not care which half it came from — the exact-code rule
// in membershipMatchesCourse runs against every stored entry — so a returning
// provider re-prices her routes with no action on her part.
function MembershipPicker({ lang, value, onChange, courses }) {
  const isZh = lang === 'zh';
  const held = React.useMemo(() => (Array.isArray(value) ? value : []), [value]);

  const options = React.useMemo(
    () => (window.membershipProviderOptions ? window.membershipProviderOptions(courses) : []),
    [courses],
  );
  const codeSet = React.useMemo(
    () => new Set(options.map(o => o.code.toLowerCase())),
    [options],
  );

  // Split what is stored into "a chip owns this" and "free text owns this".
  // Anything the options no longer cover lands in the text box rather than
  // vanishing (see the header note).
  const selected = held.filter(v => codeSet.has(String(v).trim().toLowerCase()));
  const others = held.filter(v => !codeSet.has(String(v).trim().toLowerCase()));

  // The free-text half is edited as TEXT, not as an array: re-deriving the
  // string from the array on every keystroke would fight the caret the moment
  // she types a comma. Seeded once, exactly like the field it replaces.
  const [otherText, setOtherText] = React.useState(() => others.join('、'));

  const emit = (nextSelected, nextOtherText) => {
    const parsed = String(nextOtherText || '').split(/[,、，]/).map(s => s.trim()).filter(Boolean);
    const merged = window.sanitiseMemberships
      ? window.sanitiseMemberships([...nextSelected, ...parsed])
      : [...nextSelected, ...parsed];
    onChange?.(merged);
  };

  const toggle = (code) => {
    const on = selected.some(v => String(v).toLowerCase() === code.toLowerCase());
    const next = on
      ? selected.filter(v => String(v).toLowerCase() !== code.toLowerCase())
      : [...selected, code];
    emit(next, otherText);
  };

  const max = window.MEMBERSHIP_MAX_ENTRIES || 12;
  const atCap = held.length >= max;

  return (
    <div className="onb-field" style={{ marginTop: 4 }}>
      <div className="auth-label mono">
        {isZh ? '專業會籍（選填）' : 'Memberships (optional)'}
      </div>

      {options.length > 0 ? (
        <React.Fragment>
          <div className="onb-roles" role="group"
            aria-label={isZh ? '設會員價的機構' : 'Bodies with member pricing'}>
            {options.map(o => {
              const on = selected.some(v => String(v).toLowerCase() === o.code.toLowerCase());
              return (
                <button key={o.code} type="button"
                  className={'onb-chip' + (on ? ' on' : '')}
                  aria-pressed={on}
                  /* The count is the honest reason this option exists — it is
                     how many listed courses her answer would re-price. */
                  title={isZh ? `${o.courses} 個課程設會員價` : `${o.courses} listed courses price by membership`}
                  disabled={!on && atCap}
                  onClick={() => toggle(o.code)}>
                  {isZh ? o.zh : o.en}
                </button>
              );
            })}
          </div>
          <div className="muted-2" style={{ fontSize: 11.5, lineHeight: 1.5, marginTop: 6 }}>
            {isZh
              ? '這些機構的課程設會員／非會員價。揀咗之後,補分路線會用你實際付的價錢計,而唔係顯示「由…起」。'
              : 'These bodies price by membership. Tick yours and your route is costed at what you actually pay, instead of showing “from”.'}
          </div>
        </React.Fragment>
      ) : (
        // Catalogue not loaded (offline cold start) — say so rather than
        // rendering an empty group that looks like "no memberships exist".
        <div className="muted-2" style={{ fontSize: 11.5, lineHeight: 1.5 }}>
          {isZh ? '課程目錄載入中,你仍可在下面自行輸入。' : 'Catalogue still loading — you can type yours below.'}
        </div>
      )}

      <div style={{ marginTop: 10 }}>
        <AuthField
          label={isZh ? '其他會籍（選填）' : 'Other memberships (optional)'}
          value={otherText}
          onChange={(v) => { setOtherText(v); emit(selected, v); }}
          /* ‼️ THE EXAMPLE MUST NOT NAME A BODY THAT IS ALREADY A CHIP. The
             options are catalogue-derived, so any society named here could
             become one tomorrow — and telling her to type something she can
             tick two lines above is exactly the kind of contradiction the old
             free-text field was full of. Keep it generic. */
          placeholder={isZh ? '例如：其他專科學會' : 'e.g. another specialty society'} />
        {/* ‼️ SAYS WHAT IT DOES *NOT* DO. Promising that any typed society will
            be priced is the failure this whole change exists to end. */}
        <div className="muted-2" style={{ fontSize: 11.5, lineHeight: 1.5, marginTop: 6 }}>
          {isZh
            ? '用逗號分隔。上面沒有的機構可以記錄喺呢度,但因為目錄未有它們的會員價,所以不會影響計出來的費用。'
            : 'Separate with commas. Anything not listed above is recorded for your reference only — the catalogue has no member price for it, so it will not change a calculated cost.'}
        </div>
        {atCap && (
          <div className="muted-2" style={{ fontSize: 11.5, marginTop: 4 }}>
            {isZh ? `最多 ${max} 項。` : `Up to ${max} entries.`}
          </div>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// 公告 / APP NOTICE BANNER
// ─────────────────────────────────────────────────────────────────────────────
//
// One operator-authored notice, rendered at the top of Home. The admin preview
// renders THIS component with preview=true, so what the operator approves is
// literally what ships — a preview built from separate markup is a preview that
// eventually lies.
//
// ‼️‼️ THE COPY IS PLAIN TEXT. Every field below is interpolated as a React
// text node and MUST STAY THAT WAY. No dangerouslySetInnerHTML, no markdown
// renderer, no "just let admins bold a word". This is the one string in the app
// that one person types and every nurse in Hong Kong reads, at the top of her
// dashboard, in the app's official voice — which makes it the one string where
// an injection is worth someone's time. scripts/test-notice-contract.mjs fails
// the build if dangerouslySetInnerHTML appears anywhere near this code.
//
// Severity drives colour and glyph only; it carries no behaviour except that
// HomeScreen suppresses its own one-time cards while a 'critical' notice is up.
const NOTICE_SEVERITY_STYLE = {
  info:     { token: 'var(--primary)', icon: 'bell' },
  success:  { token: 'var(--success)', icon: 'check-circ' },
  warning:  { token: 'var(--warning)', icon: 'alert' },
  critical: { token: 'var(--danger)',  icon: 'alert' },
};

function NoticeBanner({ notice, lang, onDismiss, onCta, preview = false }) {
  if (!notice) return null;
  const isZh = lang === 'zh';
  // Unknown severity renders as 'info' rather than crashing or vanishing: a
  // notice that reaches the client has already been judged worth showing.
  const sev = NOTICE_SEVERITY_STYLE[notice.severity] || NOTICE_SEVERITY_STYLE.info;

  const title = isZh ? notice.title_zh : notice.title_en;
  const body = isZh ? notice.body_zh : notice.body_en;
  const ctaLabel = isZh ? notice.cta_label_zh : notice.cta_label_en;
  const hasCta = !!(notice.cta_url && ctaLabel && onCta);

  return (
    <div style={{ padding: '12px 20px 0' }}>
      <div className="card" role={notice.severity === 'critical' ? 'alert' : 'status'}
        style={{
          display: 'flex', gap: 11, alignItems: 'flex-start', padding: 14,
          border: `1px solid color-mix(in oklch, ${sev.token} 45%, transparent)`,
          background: `color-mix(in oklch, ${sev.token} 9%, var(--surface))`,
        }}>
        <div aria-hidden="true" style={{
          width: 32, height: 32, flexShrink: 0, borderRadius: 16,
          background: sev.token, color: '#fff',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <Icon name={sev.icon} size={16} stroke={2} />
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          {/* Plain text. See the block comment above before changing this. */}
          <div style={{ fontWeight: 700, fontSize: 13.5, lineHeight: 1.4 }}>{title}</div>
          <div className="muted-2" style={{ fontSize: 12.5, lineHeight: 1.5, marginTop: 3,
                                            whiteSpace: 'pre-wrap' }}>{body}</div>

          {hasCta && (
            <button type="button" className="btn btn-primary"
              style={{ marginTop: 10, padding: '6px 14px', fontSize: 12.5 }}
              disabled={preview}
              onClick={() => onCta(notice)}>
              {ctaLabel}
            </button>
          )}
        </div>

        {/* ✕ only when the notice allows it. The server refuses a dismissal of a
            non-dismissible notice too — this is the affordance, not the rule. */}
        {notice.dismissible && (
          <button type="button"
            aria-label={isZh ? '關閉' : 'Dismiss'}
            disabled={preview}
            onClick={() => !preview && onDismiss?.(notice)}
            style={{
              flexShrink: 0, appearance: 'none', border: 0, background: 'transparent',
              cursor: preview ? 'default' : 'pointer', color: 'var(--fg-3)',
              padding: 2, lineHeight: 0, opacity: preview ? 0.4 : 1,
            }}>
            <Icon name="close" size={15} stroke={1.9} />
          </button>
        )}
      </div>
    </div>
  );
}

Object.assign(window, {
  Icon, TopicPill, CatDot, CAT_COLORS, CourseCard, MiniCourseCard,
  ProgressRing, TabBar, Sheet, ScreenHeader, MembershipPicker,
  NoticeBanner, NOTICE_SEVERITY_STYLE,
  // Shared by CourseCard here and GridCourseCard in screens.jsx, so the two
  // card shapes can never drift on what a peer count means.
  PeerSignalChips,
});

// ─────────────────────────────────────────────────────────────────────────────
// Confirmation — the in-app replacement for window.confirm
// ─────────────────────────────────────────────────────────────────────────────
//
// ‼️ WHY THIS EXISTS AT ALL. On a real iPhone (Safari, 2026-09-01) tapping a
// control guarded by `window.confirm` produced NO dialog and froze the page; the
// panel finally appeared only after a reload and re-entering the screen. That
// call is SYNCHRONOUS and blocks the JS main thread, so if WebKit defers
// presenting it the app is wedged with no recourse — and the queued dialog can
// surface later against a page that has moved on. RELEASE.md §94 fixed the one
// site that had device evidence; this is the primitive that removes the API from
// the rest, so the next destructive control cannot reintroduce the freeze.
//
// ‼️ AND WHY A HOOK RATHER THAN SIX COPIES OF A DIALOG. The dialog is the easy
// half. What actually goes wrong lives in the *flow*, and got it wrong once
// already in this codebase:
//
//   • the busy flag cleared on the happy line, so a thrown fetch left every
//     control disabled with a reload as the only exit
//   • a double tap sent two writes, because `busy` STATE cannot latch — two taps
//     in one tick both read the pre-render value
//   • cancel remained live while a write was already on the wire
//
// Centralising means a new call site gets all three right by construction
// instead of by review.

function useConfirm() {
  const [request, setRequest] = useState(null);
  const [busy, setBusy] = useState(false);
  // ‼️ A REF, NOT `busy`. This is checked and set in the SAME TICK; state cannot
  // stop the second of two taps that land before React re-renders.
  const inFlight = useRef(false);

  // Synchronous by design: opening the dialog must never await anything, or it
  // reintroduces exactly the "tap does nothing" symptom this replaces.
  const ask = useCallback((options) => {
    if (inFlight.current) return;
    setRequest(options || null);
  }, []);

  const cancel = useCallback(() => {
    if (inFlight.current) return;   // never strand a write already sent
    setRequest(null);
  }, []);

  const confirm = useCallback(async () => {
    if (!request || inFlight.current) return;
    inFlight.current = true;
    setBusy(true);
    try {
      await request.onConfirm?.();
    } catch (e) {
      // ‼️ A THROW MUST NOT ESCAPE. An unhandled rejection here would leave the
      // dialog up and the panel dead — the failure this whole module is about.
      // Sites that handle their own errors return normally and never land here.
      console.error('[confirm]', e);
      if (typeof showVitalToast === 'function') {
        showVitalToast(request.failureText || 'Action failed', 'warning');
      }
    } finally {
      // ‼️ FINALLY, NOT THE HAPPY PATH.
      inFlight.current = false;
      setBusy(false);
      setRequest(null);
    }
  }, [request]);

  return { request, busy, ask, cancel, confirm };
}

// ‼️ `.register-dialog-backdrop` IS `position: fixed`, AND THAT IS LOAD-BEARING.
// These dialogs are opened from panels living inside `.scroll` — an
// `overflow-x: hidden` container with its own stacking context (see the z-index
// hygiene block in styles.css). An absolutely positioned dialog there scrolls
// away with the list behind it and is clipped by it. Do not swap this for the
// `.sheet` classes without re-checking that.
function ConfirmDialog({ request, busy, lang = 'en', onCancel, onConfirm }) {
  const zh = lang === 'zh';

  // Escape cancels — but never mid-write, or the admin dismisses a dialog whose
  // call is already out.
  useEffect(() => {
    if (!request) return undefined;
    const onKey = (e) => { if (e.key === 'Escape' && !busy) onCancel(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [request, busy, onCancel]);

  if (!request) return null;

  const danger = request.tone === 'danger';
  // ‼️ minHeight 44 — Apple's minimum. The destructive controls on these panels
  // were the smallest targets on their screens before §94.
  const base = {
    minHeight: 44, padding: '10px 18px', borderRadius: 10, fontSize: 14,
    fontWeight: 500, border: 0, cursor: busy ? 'wait' : 'pointer',
  };
  return (
    <div className="register-dialog-backdrop" onClick={() => { if (!busy) onCancel(); }}>
      <div className="register-dialog" role="dialog" aria-modal="true"
        aria-label={request.title}
        style={{ maxWidth: 380, padding: '22px 20px 18px' }}
        onClick={(e) => e.stopPropagation()}>
        <div className="h-section" style={{ marginBottom: request.subject ? 10 : 8 }}>
          {request.title}
        </div>

        {/* What is being acted on — the reason this is a dialog and not a
            yes/no. A list of similar rows makes "this item" unanswerable. */}
        {request.subject && (
          <div style={{
            padding: 10, borderRadius: 10, background: 'var(--surface)',
            border: '0.5px solid var(--border)', marginBottom: 12,
          }}>
            <div style={{ fontSize: 14.5, fontWeight: 500 }}>{request.subject}</div>
            {request.subjectDetail && (
              <div className="muted" style={{ fontSize: 12 }}>{request.subjectDetail}</div>
            )}
          </div>
        )}

        {request.body && (
          <div className="muted" style={{ fontSize: 12.5, lineHeight: 1.55, marginBottom: 16, whiteSpace: 'pre-line' }}>
            {request.body}
          </div>
        )}

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button type="button" onClick={onCancel} disabled={busy}
            style={{ ...base, background: 'var(--surface)', color: 'var(--fg-1)',
                     border: '0.5px solid var(--border)', opacity: busy ? 0.5 : 1 }}>
            {request.cancelLabel || (zh ? '取消' : 'Cancel')}
          </button>
          {/* `disabled` is the visible half of the double-submit guard; the ref
              latch in useConfirm is the half that actually holds. */}
          <button type="button" onClick={onConfirm} disabled={busy}
            style={{ ...base, background: danger ? 'var(--danger)' : 'var(--primary)',
                     color: '#fff', opacity: busy ? 0.6 : 1 }}>
            {busy
              ? (zh ? '處理中…' : 'Working…')
              : (request.confirmLabel || (zh ? '確認' : 'Confirm'))}
          </button>
        </div>
      </div>
    </div>
  );
}

;Object.assign(typeof window !== 'undefined' ? window : globalThis, { Icon, TopicPill, CAT_COLORS, CatDot, Toggle, SourceBadge, hashHue, AVATAR_EMOJI, UserAvatar, PeerSignalChips, restrictionCardState, RestrictionChips, CourseCard, MiniCourseCard, ProgressRing, TabBar, Sheet, DP_MONTHS_EN, DP_ITEM_H, DP_VISIBLE, dpPad2, dpDaysInMonth, dpRangeItems, dpParse, dpClamp, _dpAudioCtx, dpEnsureAudio, dpTick, dpHapticsOn, dpHapticStart, dpHapticChange, dpHapticEnd, WheelColumn, WheelDatePicker, DateField, TP_MIN_STEP, tpTo24, tpReadClocks, tpParseOne, tpParseRange, tpFmt, tpFmtRange, tpMins, tpMinuteItems, tpCanonical, tpSeed, WheelTimePicker, TimeRangeField, TimeField, ScreenHeader, MembershipPicker, NOTICE_SEVERITY_STYLE, NoticeBanner, useConfirm, ConfirmDialog });
